import { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import { RESSOURCES_JSON, appliquerSurcharges } from "i18n";

const LANGUES = ["fr", "ar", "en"];
// Formes plurielles propres à l'arabe (i18next) : absentes en fr/en, masquées par défaut
const FORME_PLURIEL_AR = /_(zero|two|few|many)$/;

// { menu: { dashboard: "..." } } → { "menu.dashboard": "..." }
function aplatir(obj, prefixe = "", res = {}) {
  Object.entries(obj || {}).forEach(([k, v]) => {
    const cle = prefixe ? `${prefixe}.${k}` : k;
    if (v && typeof v === "object") aplatir(v, cle, res);
    else res[cle] = String(v);
  });
  return res;
}

// Valeurs d'origine des JSON, par clé : { "menu.dashboard": { fr, ar, en } }
const REFERENCE = (() => {
  const plats = Object.fromEntries(LANGUES.map((l) => [l, aplatir(RESSOURCES_JSON[l])]));
  const cles = new Set(LANGUES.flatMap((l) => Object.keys(plats[l])));
  const ref = {};
  [...cles].sort().forEach((cle) => {
    ref[cle] = Object.fromEntries(LANGUES.map((l) => [l, plats[l][cle] ?? ""]));
  });
  return ref;
})();

const variables = (s) => (String(s || "").match(/\{\{\s*[\w.]+\s*\}\}/g) || []).map((v) => v.replace(/\s/g, "")).sort().join(",");

function Traductions() {
  const { user } = useAuth();
  const [surcharges, setSurcharges] = useState({}); // cle → {fr, ar, en} (null = JSON)
  const [modifs, setModifs] = useState({});         // cle → {fr, ar, en} en cours d'édition
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [manquantes, setManquantes] = useState(false);
  const [formesAr, setFormesAr] = useState(false);
  const [message, setMessage] = useState(null);

  const chargerLignes = (lignes) => {
    const map = {};
    lignes.forEach((l) => { map[l.cle] = { fr: l.fr, ar: l.ar, en: l.en }; });
    setSurcharges(map);
  };

  useEffect(() => {
    api.get("/traductions")
      .then(({ data }) => chargerLignes(data))
      .catch(() => setMessage({ type: "error", texte: "Impossible de charger les traductions" }))
      .finally(() => setLoading(false));
  }, []);

  const nbModifs = Object.keys(modifs).length;

  useEffect(() => {
    if (!nbModifs) return undefined;
    const avertir = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", avertir);
    return () => window.removeEventListener("beforeunload", avertir);
  }, [nbModifs]);

  // Valeur affichée : édition en cours > surcharge DB > JSON
  const valeur = (cle, lng) => {
    if (modifs[cle]) return modifs[cle][lng];
    const s = surcharges[cle]?.[lng];
    return s ?? REFERENCE[cle]?.[lng] ?? "";
  };

  const changer = (cle, lng, v) => {
    setModifs((m) => {
      const ligne = { ...(m[cle] || Object.fromEntries(LANGUES.map((l) => [l, valeur(cle, l)]))), [lng]: v };
      const actuel = Object.fromEntries(LANGUES.map((l) => [l, surcharges[cle]?.[l] ?? REFERENCE[cle][l]]));
      const suivant = { ...m };
      if (LANGUES.every((l) => ligne[l] === actuel[l])) delete suivant[cle];
      else suivant[cle] = ligne;
      return suivant;
    });
  };

  const lignes = useMemo(() => {
    const q = search.trim().toLowerCase();
    return Object.keys(REFERENCE).filter((cle) => {
      if (!formesAr && FORME_PLURIEL_AR.test(cle)) return false;
      if (manquantes && LANGUES.every((l) => valeur(cle, l).trim() !== "")) return false;
      if (!q) return true;
      return cle.toLowerCase().includes(q) || LANGUES.some((l) => valeur(cle, l).toLowerCase().includes(q));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, manquantes, formesAr, modifs, surcharges]);

  const sauvegarder = async () => {
    // Une valeur identique au JSON est envoyée à null : pas de surcharge inutile
    const payload = Object.entries(modifs).map(([cle, v]) => ({
      cle,
      ...Object.fromEntries(LANGUES.map((l) => [l, v[l] === REFERENCE[cle][l] ? null : v[l]])),
    }));
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await api.post("/traductions", payload);
      chargerLignes(data);
      const pourI18n = Object.fromEntries(LANGUES.map((l) => [l, {}]));
      payload.forEach((p) => LANGUES.forEach((l) => { pourI18n[l][p.cle] = p[l]; }));
      appliquerSurcharges(pourI18n);
      setModifs({});
      setMessage({ type: "success", texte: `${payload.length} traduction(s) enregistrée(s)` });
    } catch (error) {
      setMessage({ type: "error", texte: error.response?.data?.message || "Erreur lors de l'enregistrement" });
    } finally {
      setSaving(false);
    }
  };

  const reinitialiser = async (cle) => {
    if (!window.confirm(`Revenir aux valeurs d'origine pour « ${cle} » ?`)) return;
    try {
      await api.delete(`/traductions/${encodeURIComponent(cle)}`);
      setSurcharges((s) => { const n = { ...s }; delete n[cle]; return n; });
      setModifs((m) => { const n = { ...m }; delete n[cle]; return n; });
      appliquerSurcharges(Object.fromEntries(LANGUES.map((l) => [l, { [cle]: null }])));
    } catch (error) {
      setMessage({ type: "error", texte: error.response?.data?.message || "Erreur lors de la réinitialisation" });
    }
  };

  if (user?.role !== "admin") {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={6}><MDTypography variant="h6">Accès réservé aux administrateurs</MDTypography></MDBox>
      </DashboardLayout>
    );
  }

  const th = {
    padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#9e9e9e",
    textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "2px solid #f5f5f5",
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>

              <MDBox mx={2} mt={-3} py={3} px={3} sx={{
                background: "linear-gradient(135deg, #37474f 0%, #455a64 100%)",
                borderRadius: "12px", boxShadow: "0 4px 20px rgba(55,71,79,0.4)",
                display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2,
              }}>
                <MDBox>
                  <MDTypography variant="h5" color="white" fontWeight="bold">Traductions</MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                    {Object.keys(REFERENCE).filter((c) => formesAr || !FORME_PLURIEL_AR.test(c)).length} clés · {Object.keys(surcharges).length} modifiée(s)
                  </MDTypography>
                </MDBox>
                <MDButton variant="contained" size="medium" disabled={!nbModifs || saving} onClick={sauvegarder}
                  sx={{ background: "#fff", color: "#37474f", fontWeight: 700, borderRadius: "10px", "&:hover": { background: "#eceff1" } }}>
                  <Icon sx={{ mr: 0.5 }}>save</Icon>
                  {saving ? "Enregistrement..." : `Sauvegarder${nbModifs ? ` (${nbModifs})` : ""}`}
                </MDButton>
              </MDBox>

              <MDBox pt={3} px={3} pb={3}>
                <MDBox mb={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <MDBox sx={{ maxWidth: 400, flex: 1 }}>
                    <MDInput fullWidth label="🔍  Rechercher une clé ou un texte..." value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                  </MDBox>
                  <FormControlLabel label="Traductions manquantes"
                    control={<Checkbox checked={manquantes} onChange={(e) => setManquantes(e.target.checked)} />} />
                  <FormControlLabel label="Afficher les formes plurielles AR"
                    control={<Checkbox checked={formesAr} onChange={(e) => setFormesAr(e.target.checked)} />} />
                </MDBox>

                {message && (
                  <MDBox mb={2} px={2} py={1} sx={{
                    borderRadius: "8px",
                    background: message.type === "success" ? "#e8f5e9" : "#ffebee",
                    color: message.type === "success" ? "#2e7d32" : "#c62828", fontSize: 14,
                  }}>
                    {message.texte}
                  </MDBox>
                )}

                {loading ? (
                  <MDTypography variant="body2" color="text">Chargement...</MDTypography>
                ) : (
                  <MDBox sx={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                      <thead>
                        <tr>
                          <th style={{ ...th, width: "20%" }}>Clé</th>
                          <th style={th}>FR</th>
                          <th style={th}>AR</th>
                          <th style={th}>EN</th>
                          <th style={{ ...th, width: 50 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {lignes.map((cle) => {
                          const surchargee = !!surcharges[cle];
                          const varsFr = variables(valeur(cle, "fr"));
                          return (
                            <tr key={cle} style={{ borderBottom: "1px solid #f5f5f5", background: modifs[cle] ? "#fffde7" : "transparent" }}>
                              <td style={{ padding: "8px 12px", verticalAlign: "top" }}>
                                <code style={{ fontSize: 12, color: "#344767", wordBreak: "break-all" }}>{cle}</code>
                              </td>
                              {LANGUES.map((l) => {
                                const v = valeur(cle, l);
                                const estSurchargee = surcharges[cle]?.[l] != null;
                                const varsKo = l !== "fr" && v && variables(v) !== varsFr;
                                return (
                                  <td key={l} style={{ padding: "6px 8px", verticalAlign: "top" }}>
                                    <Tooltip title={estSurchargee ? `Original : ${REFERENCE[cle][l] || "(vide)"}` : ""} placement="top">
                                      <TextField fullWidth multiline size="small" value={v}
                                        onChange={(e) => changer(cle, l, e.target.value)}
                                        error={!!varsKo}
                                        helperText={varsKo ? "Variables {{…}} différentes du FR" : ""}
                                        inputProps={{ dir: l === "ar" ? "rtl" : "ltr", lang: l }}
                                        sx={{ "& .MuiOutlinedInput-root": { background: estSurchargee ? "#e3f2fd" : "transparent" } }} />
                                    </Tooltip>
                                  </td>
                                );
                              })}
                              <td style={{ padding: "8px", verticalAlign: "top" }}>
                                {surchargee && (
                                  <Tooltip title="Revenir aux valeurs d'origine">
                                    <MDBox onClick={() => reinitialiser(cle)} sx={{
                                      width: 32, height: 32, borderRadius: "8px", background: "#ffebee", color: "#c62828",
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                      cursor: "pointer", "&:hover": { background: "#ffcdd2" },
                                    }}>
                                      <Icon sx={{ fontSize: 16 }}>restart_alt</Icon>
                                    </MDBox>
                                  </Tooltip>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Traductions;
